/**
 * Centralized Marketplace Constants (Frontend)
 *
 * IMPORTANT: This file is a copy of the backend marketplace constants.
 * The backend version is the source of truth at:
 *   shared/lib/marketplace-constants.ts
 *
 * If you modify this file, you MUST also update the backend copy!
 * The two files must stay in sync.
 *
 * @see shared/lib/marketplace-constants.ts
 */

// =============================================================================
// MARKETPLACE DEFINITIONS
// =============================================================================

/**
 * Marketplace configuration interface
 */
export interface MarketplaceConfig {
  /** Unique identifier for the marketplace */
  id: string;
  /** Display name for UI */
  name: string;
  /** XRPL broker address */
  address: string;
  /** Fee percentage (e.g., 1.589 for 1.589%) */
  feePercentage: number;
  /** Whether this is our own marketplace (we can execute broker transactions) */
  isOwnMarketplace: boolean;
}

/**
 * All supported marketplaces
 * Keys are domain names (matching what Bithomp API returns)
 * Order matters - first one is the default for listing forms
 */
export const MARKETPLACES: Record<string, MarketplaceConfig> = {
  'xrp.deals': {
    id: 'xrp.deals',
    name: 'xrp.deals',
    // Frontend uses import.meta.env for Vite, backend uses process.env
    address: import.meta.env.VITE_DEALS_WALLET_ADDRESS || 'rMzKB1K6v5cjs6A9AVYX5UpKzce184TjYw',
    feePercentage: 1.0,
    isOwnMarketplace: true,
  },
  'xrp.cafe': {
    id: 'xrp.cafe',
    name: 'xrp.cafe',
    address: 'rpx9JThQ2y37FaGeeJP7PXDUVEXY3PHZSC',
    feePercentage: 1.589,
    isOwnMarketplace: false,
  },
  'bidds.com': {
    id: 'bidds.com',
    name: 'bidds.com',
    address: 'rpZqTPC8GvrSvEfFsUuHkmPCg29GdQuXhC',
    feePercentage: 0.75,
    isOwnMarketplace: false,
  },
  'opmarket.ai': {
    id: 'opmarket.ai',
    name: 'opmarket.ai',
    address: 'rLety3EQTmqSUppo4KcUUZTq5DuEHXNSyS',
    feePercentage: 2.0,
    isOwnMarketplace: false,
  },
} as const;

/**
 * Type for valid marketplace IDs (keys of MARKETPLACES)
 */
export type MarketplaceId = keyof typeof MARKETPLACES;

/**
 * Array of all marketplace IDs
 */
export const MARKETPLACE_IDS = Object.keys(MARKETPLACES) as MarketplaceId[];

/**
 * Array of all marketplaces for iteration
 */
export const MARKETPLACE_LIST = Object.values(MARKETPLACES);

/**
 * Set of all supported marketplace addresses for quick lookup
 */
export const SUPPORTED_MARKETPLACE_ADDRESSES = new Set(
  MARKETPLACE_LIST.map(m => m.address)
);

// =============================================================================
// INDIVIDUAL MARKETPLACE EXPORTS (for backward compatibility)
// =============================================================================

/** XRP Deals broker address (current) */
export const DEALS_BROKER_ADDRESS = MARKETPLACES['xrp.deals'].address;
/** XRP Deals fee percentage */
export const DEALS_FEE_PERCENTAGE = MARKETPLACES['xrp.deals'].feePercentage;

/**
 * Legacy XRP Deals broker addresses from previous production deployments.
 * Some NFTs may still have active offers with these as destination.
 * Used by isDealsBrokeredOffer() and DB offer sort tiebreaker.
 */
export const DEALS_LEGACY_ADDRESSES = [
  'rSALEvz6MQ9gmRhUyDZA96acqZSa4KBAv',   // old production broker
  'rDEALSphy6Jn6KgEtZzBEwxoyppC4hcuZa',   // consolidated production (rDEALS vanity)
] as const;

/** All XRP Deals broker addresses (current + legacy) */
export const ALL_DEALS_ADDRESSES = new Set([
  DEALS_BROKER_ADDRESS,
  ...DEALS_LEGACY_ADDRESSES,
]);

/** xrp.cafe broker address */
export const CAFE_BROKER_ADDRESS = MARKETPLACES['xrp.cafe'].address;
/** xrp.cafe fee percentage */
export const CAFE_FEE_PERCENTAGE = MARKETPLACES['xrp.cafe'].feePercentage;

/** bidds.com broker address */
export const BIDDS_BROKER_ADDRESS = MARKETPLACES['bidds.com'].address;
/** bidds.com fee percentage */
export const BIDDS_FEE_PERCENTAGE = MARKETPLACES['bidds.com'].feePercentage;

/** opmarket.ai broker address */
export const OPMARKET_BROKER_ADDRESS = MARKETPLACES['opmarket.ai'].address;
/** opmarket.ai fee percentage */
export const OPMARKET_FEE_PERCENTAGE = MARKETPLACES['opmarket.ai'].feePercentage;

// =============================================================================
// LAUNCHPAD CONSTANTS
// =============================================================================

/**
 * Default service fee percentage for new launches.
 * Each launch can override this via launches.service_fee_percent.
 */
export const DEFAULT_MINT_SERVICE_FEE_PERCENT = 5;

/**
 * Calculate the platform service fee for a mint price.
 * Uses Math.ceil so the platform never loses a drop to rounding.
 * Returns 0 for free mints or 0% fee.
 */
export function calculateMintServiceFee(mintPriceDrops: number, feePercent: number): number {
  if (mintPriceDrops <= 0 || feePercent <= 0) return 0;
  return Math.ceil(mintPriceDrops * feePercent / 100);
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get marketplace config by broker address
 * @param address - XRPL broker address
 * @returns MarketplaceConfig or undefined if not a known marketplace
 */
export function getMarketplaceByAddress(address: string | null | undefined): MarketplaceConfig | undefined {
  if (!address) return undefined;
  return MARKETPLACE_LIST.find(m => m.address === address);
}

/**
 * Get marketplace config by ID/domain name
 * @param id - Marketplace ID/domain (e.g., 'xrp.cafe', 'xrp.deals', 'bidds.com')
 * @returns MarketplaceConfig or undefined if not a known marketplace
 */
export function getMarketplaceById(id: string | null | undefined): MarketplaceConfig | undefined {
  if (!id) return undefined;
  return MARKETPLACES[id];
}

/**
 * Get marketplace name from broker address
 * @param destination - The destination address from the offer
 * @returns The marketplace name ('xrp.deals', 'xrp.cafe', etc.) or 'P2P' for no destination, 'Other' for unknown
 */
export function getMarketplaceName(destination: string | null | undefined): string {
  if (!destination) return 'P2P';
  const marketplace = getMarketplaceByAddress(destination);
  return marketplace?.name || 'Other';
}

/**
 * Get the fee percentage for a given marketplace
 * @param destination - The destination address from the offer
 * @returns The fee percentage (0 for P2P or unknown marketplaces)
 */
export function getMarketplaceFeePercentage(destination: string | null | undefined): number {
  if (!destination) return 0;
  const marketplace = getMarketplaceByAddress(destination);
  return marketplace?.feePercentage || 0;
}

/**
 * Check if a destination address is a supported marketplace
 * @param destination - The destination address from the offer
 * @returns true if the destination is a supported marketplace or P2P (no destination)
 */
export function isSupportedMarketplace(destination: string | null | undefined): boolean {
  if (!destination) return true; // P2P is always supported
  return SUPPORTED_MARKETPLACE_ADDRESSES.has(destination);
}

/**
 * Check if an offer is relevant to our platform (should be processed/stored)
 * We process offers that are:
 * - Direct P2P offers (no destination) - anyone can accept
 * - Any supported marketplace-brokered offers
 *
 * @param destination - The destination address from the offer (null/undefined for direct P2P)
 * @returns true if the offer should be processed/stored, false otherwise
 */
export function isRelevantOffer(destination: string | null | undefined): boolean {
  return isSupportedMarketplace(destination);
}

/**
 * Check if an offer is destined for our XRP Deals broker
 * @param destination - The destination address from the offer
 * @returns true if the offer is destined for XRP Deals broker
 */
export function isDealsBrokeredOffer(destination: string | null | undefined): boolean {
  if (!destination) return false;
  return ALL_DEALS_ADDRESSES.has(destination);
}

/**
 * Check if an offer is cafe-brokered
 * @param destination - The destination address from the offer
 * @returns true if the offer is cafe-brokered
 */
export function isCafeBrokeredOffer(destination: string | null | undefined): boolean {
  return destination === CAFE_BROKER_ADDRESS;
}

// =============================================================================
// FEE CALCULATION FUNCTIONS
// =============================================================================

/**
 * Calculate the marketplace fee for a given sell amount.
 * Uses Math.round to match how marketplace brokers (e.g. xrp.cafe) calculate fees.
 * (Math.floor would short the broker by up to 1 drop, causing them to refuse
 * to execute the trade — confirmed with NFT 0019C3...E37BA0 where a 1.23 XRP
 * sell offer produced a buy offer of 1.249544 instead of the correct 1.249545.)
 *
 * @param sellAmount - The sell offer amount in XRP drops
 * @param destination - The marketplace destination address
 * @returns The marketplace fee in XRP drops
 */
export function calculateMarketplaceFee(sellAmount: number, destination: string | null | undefined): number {
  const feePercentage = getMarketplaceFeePercentage(destination);
  return Math.round(sellAmount * (feePercentage / 100));
}

/**
 * Calculate the total amount a buyer pays for any marketplace.
 * Formula: buyAmount = sellAmount + round(sellAmount * feePercentage / 100)
 *
 * Uses calculateMarketplaceFee (which uses Math.round, matching broker behavior)
 * to ensure the buy offer amount exactly matches what the broker expects.
 *
 * @param sellAmount - The sell offer amount in XRP drops
 * @param destination - The marketplace destination address
 * @returns The total buy amount including marketplace fee in XRP drops
 */
export function calculateBuyAmount(sellAmount: number, destination: string | null | undefined): number {
  return sellAmount + calculateMarketplaceFee(sellAmount, destination);
}

/**
 * Calculate the sell amount when accepting a buy offer through a broker
 * Formula: sellAmount = buyAmount * (1 - feePercentage / 100)
 *
 * When a seller accepts a buy offer through a marketplace:
 * - Buyer already placed offer for X amount (buyer pays exactly X)
 * - Seller pays the marketplace fee
 * - Seller creates sell offer for X * (1 - fee%) so marketplace keeps the difference
 *
 * Example: 7 XRP buy offer with 1% fee
 * - Seller receives: 7 * 0.99 = 6.93 XRP
 * - Marketplace fee: 7 * 0.01 = 0.07 XRP
 *
 * @param buyAmount - The buy offer amount in XRP drops
 * @param destination - The marketplace destination address
 * @returns The sell amount that gives the marketplace its fee
 */
export function calculateSellAmountForBuyOffer(buyAmount: number, destination: string | null | undefined): number {
  const feePercentage = getMarketplaceFeePercentage(destination);
  if (feePercentage === 0) return buyAmount;
  return Math.floor(buyAmount * (1 - feePercentage / 100));
}

/**
 * Format amount with fee breakdown for display
 *
 * @param sellAmount - The sell amount in XRP drops
 * @param destination - The marketplace destination address
 * @returns Object with amounts in drops and formatted XRP strings
 */
export function formatFeeBreakdown(sellAmount: number, destination: string | null | undefined): {
  sellAmount: number;
  marketplaceFee: number;
  totalAmount: number;
  sellAmountXRP: string;
  marketplaceFeeXRP: string;
  totalAmountXRP: string;
  feePercentage: number;
  marketplaceName: string;
} {
  const feePercentage = getMarketplaceFeePercentage(destination);
  const marketplaceFee = calculateMarketplaceFee(sellAmount, destination);
  const totalAmount = calculateBuyAmount(sellAmount, destination);
  const marketplaceName = getMarketplaceName(destination);

  return {
    sellAmount,
    marketplaceFee,
    totalAmount,
    sellAmountXRP: (sellAmount / 1_000_000).toFixed(6),
    marketplaceFeeXRP: (marketplaceFee / 1_000_000).toFixed(6),
    totalAmountXRP: (totalAmount / 1_000_000).toFixed(6),
    feePercentage,
    marketplaceName,
  };
}

// =============================================================================
// BACKWARD COMPATIBILITY EXPORTS
// These maintain compatibility with existing code that uses cafe-specific functions
// =============================================================================

/** @deprecated Use calculateBuyAmount(sellAmount, CAFE_BROKER_ADDRESS) instead */
export function calculateCafeBuyAmount(sellAmount: number): number {
  return calculateBuyAmount(sellAmount, CAFE_BROKER_ADDRESS);
}

/** @deprecated Use calculateMarketplaceFee(sellAmount, CAFE_BROKER_ADDRESS) instead */
export function calculateCafeFee(sellAmount: number): number {
  return calculateMarketplaceFee(sellAmount, CAFE_BROKER_ADDRESS);
}

/** @deprecated Use calculateBuyAmount(sellAmount, DEALS_BROKER_ADDRESS) instead */
export function calculateDealsBuyAmount(sellAmount: number): number {
  return calculateBuyAmount(sellAmount, DEALS_BROKER_ADDRESS);
}

/** @deprecated Use calculateMarketplaceFee(sellAmount, DEALS_BROKER_ADDRESS) instead */
export function calculateDealsFee(sellAmount: number): number {
  return calculateMarketplaceFee(sellAmount, DEALS_BROKER_ADDRESS);
}

/** @deprecated Use calculateSellAmountForBuyOffer(buyAmount, DEALS_BROKER_ADDRESS) instead */
export function calculateDealsSellAmountForBuyOffer(buyAmount: number): number {
  return calculateSellAmountForBuyOffer(buyAmount, DEALS_BROKER_ADDRESS);
}

// =============================================================================
// UI HELPERS
// =============================================================================

/**
 * Marketplace options for sell/offer form dropdowns
 * Values are domain names matching the MARKETPLACES keys
 * Includes all supported marketplaces plus P2P option
 */
export const MARKETPLACE_OPTIONS = [
  { value: 'xrp.deals', label: `xrp.deals (${DEALS_FEE_PERCENTAGE}% fee)`, destination: DEALS_BROKER_ADDRESS },
  { value: 'xrp.cafe', label: `xrp.cafe (${CAFE_FEE_PERCENTAGE}% fee)`, destination: CAFE_BROKER_ADDRESS },
  { value: 'bidds.com', label: `bidds.com (${BIDDS_FEE_PERCENTAGE}% fee)`, destination: BIDDS_BROKER_ADDRESS },
  { value: 'opmarket.ai', label: `opmarket.ai (${OPMARKET_FEE_PERCENTAGE}% fee)`, destination: OPMARKET_BROKER_ADDRESS },
  { value: 'p2p', label: 'Direct P2P (no fee)', destination: null },
] as const;

/**
 * Get marketplace option by value
 */
export function getMarketplaceOption(value: string) {
  return MARKETPLACE_OPTIONS.find(opt => opt.value === value);
}

/**
 * Get marketplace option by destination address
 */
export function getMarketplaceOptionByDestination(destination: string | null | undefined) {
  if (!destination) return MARKETPLACE_OPTIONS.find(opt => opt.value === 'p2p');
  return MARKETPLACE_OPTIONS.find(opt => opt.destination === destination);
}
